/**
 * Phase F11 FE-1 — proctor SDK client.
 *
 * High-level wrapper that the F11 components call. Three flows:
 *
 *   1. `uploadProctorArtifact(blob, kind, mime)` — mint a signed URL,
 *      PUT the bytes to the bucket, confirm via the BE. One round-trip
 *      from the caller's perspective.
 *
 *   2. `recordProctorLifecycleEvent(kind, metadata?)` — single-shot
 *      event record. Failures are swallowed (logged via the optional
 *      `onTransportError` callback) — proctor telemetry must never
 *      break the assessment runtime even on flaky connectivity.
 *
 *   3. `isProctorProfileActive(profile)` — small predicate the apply
 *      portal uses to decide whether to render the consent gate.
 *
 * The client is constructed once per attempt session via
 * `createProctorClient(token)` so the token is captured once and not
 * threaded through every call site.
 */
import {
  commitProctorUpload,
  mintProctorUploadUrl,
  recordProctorEvent,
  uploadProctorArtifactBytes,
} from "@/lib/api";
import type {
  PublicCommitProctorUploadResponse,
  PublicProctorArtifactKind,
  PublicProctorEventKind,
  PublicRecordProctorEventResponse,
} from "@eduviaos/shared/types";

/** Per-attempt proctor client. */
export interface ProctorClient {
  /**
   * Run the full mint → PUT → commit dance for one artifact.
   * Throws if any leg fails. Callers wire this into a TanStack
   * mutation so retries / backoff are surfaced uniformly with
   * the rest of the assessment runner.
   */
  uploadArtifact(args: {
    blob: Blob;
    kind: PublicProctorArtifactKind;
    mimeType: string;
    capturedAt?: Date;
    capturedMetadata?: Record<string, unknown>;
  }): Promise<PublicCommitProctorUploadResponse>;

  /**
   * Fire-and-forget lifecycle event. Failures don't throw — the SDK
   * surfaces them via `onTransportError` so the runner can keep going
   * even on flaky telemetry connectivity.
   */
  recordEvent(
    kind: PublicProctorEventKind,
    metadata?: Record<string, unknown>,
  ): Promise<PublicRecordProctorEventResponse | null>;
}

/** Build a client bound to a single apply token. */
export function createProctorClient(
  token: string,
  options: { onTransportError?: (err: unknown, ctx: ProctorTransportContext) => void } = {},
): ProctorClient {
  const { onTransportError } = options;

  async function uploadArtifact(args: {
    blob: Blob;
    kind: PublicProctorArtifactKind;
    mimeType: string;
    capturedAt?: Date;
    capturedMetadata?: Record<string, unknown>;
  }) {
    const { blob, kind, mimeType, capturedAt = new Date(), capturedMetadata } = args;

    // Step 1 — mint signed PUT URL (BE persists pending_upload row).
    const minted = await mintProctorUploadUrl(token, {
      kind,
      mime_type: mimeType,
      captured_at: capturedAt.toISOString(),
      captured_metadata: capturedMetadata ?? null,
    });

    // Step 2 — PUT the bytes directly to the bucket. The signed URL
    // is locked to `mimeType` + `max_size_bytes`, so a 1.1 MB JPEG on
    // a 1 MB cap fails at the bucket — not the BE.
    if (blob.size > minted.max_size_bytes) {
      throw new Error(
        `Artifact byte size (${blob.size}) exceeds the BE-declared cap (${minted.max_size_bytes}).`,
      );
    }
    await uploadProctorArtifactBytes(minted.put_url, blob, minted.required_headers);

    // Step 3 — confirm via the BE so the row flips to ``committed``.
    const committed = await commitProctorUpload(token, minted.artifact_id);
    return committed;
  }

  async function recordEvent(kind: PublicProctorEventKind, metadata?: Record<string, unknown>) {
    try {
      return await recordProctorEvent(token, {
        kind,
        occurred_at: new Date().toISOString(),
        metadata: metadata ?? null,
      });
    } catch (err) {
      onTransportError?.(err, { stage: "record_event", kind });
      return null;
    }
  }

  return { uploadArtifact, recordEvent };
}

/** Transport-error context surfaced via the optional callback. */
export interface ProctorTransportContext {
  stage: "mint_upload_url" | "put_bytes" | "commit_upload" | "record_event";
  kind?: PublicProctorArtifactKind | PublicProctorEventKind;
}

/**
 * Predicate for the apply portal: does this attempt's proctor profile
 * require the FE proctor SDK to engage?
 *
 * The BE owns the catalogue of profile slugs; the FE only cares whether
 * to engage at all. Anything other than `off` / empty / unknown engages
 * the SDK.
 */
export function isProctorProfileActive(profile: string | null | undefined): boolean {
  if (!profile) return false;
  const normalised = profile.trim().toLowerCase();
  if (normalised === "" || normalised === "off" || normalised === "none") return false;
  return true;
}
